import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_schoolSetupMeetingCategoryMutation,
  useGET_schoolSetupMeetingCategoryByIdQuery,
  usePATCH_schoolSetupMeetingCategoryMutation,
  usePOST_schoolSetupMeetingCategoryMutation,
} from 'redux/api/setupApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/meetingCategoryModel'
import { crudColorMap, crudIconMap, titleToSlug } from 'utils/helpers'

import { ActionType } from 'types'
import useCrudData from 'utils/customHooks/useCrudData'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const MeetingCategoryForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug ='/setup/meeting-categories/list'
  const editSlug = `/setup/meeting-categories/list/edit/${id}`

  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    entityName: `${t('common.meeting-categories')}`,
    entityId: id,
    getEntityQuery: useGET_schoolSetupMeetingCategoryByIdQuery,
    createEntityMutation: usePOST_schoolSetupMeetingCategoryMutation,
    editEntityMutation: usePATCH_schoolSetupMeetingCategoryMutation,
    deleteEntityMutation: useDELETE_schoolSetupMeetingCategoryMutation
  })

  const {
    title,
    slug
  } = formModel()
  
  const serverValues = entity?.id ? {
    ...initialValues,
    ...entity
  } : initialValues


  return (
    <Formik
      enableReinitialize
      initialValues={serverValues}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        const data: any = {
          ...values,
          slug: serverValues?.slug || titleToSlug(values?.title)
        }

        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
          <Grid item xs={12} md={12}>
            <InputField fieldProps={{ ...title }} disabled={viewOnly} />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default MeetingCategoryForm
